import React from 'react';
/* eslint-disable */
import { createScaffolderFieldExtension } from '@backstage/plugin-scaffolder-react';
/* eslint-enable */
import {
  configApiRef,
  identityApiRef,
  useApi
} from '@backstage/core-plugin-api';
import {
  makeFieldSchemaFromZod,
  scaffolderPlugin,
} from '@backstage/plugin-scaffolder';
import { CircularProgress, TextField } from '@material-ui/core';
import FormControl from '@material-ui/core/FormControl';
import Autocomplete from '@material-ui/lab/Autocomplete'; // eslint-disable-line
import { Typography } from '@mui/material';
import axios, { HttpStatusCode } from 'axios'; // eslint-disable-line
import { useAsync } from 'react-use';
// zod/v3: makeFieldSchemaFromZod is typed against zod's v3 ZodType, whose generics
// differ from v4's. The root zod is v4.
import { z } from 'zod/v3'; // eslint-disable-line

interface Repository {
  name: string;
  url: string;
}

interface GraphQLResponse {
  data: {
    search: {
      nodes: Repository[];
      pageInfo: {
        hasNextPage: boolean;
        endCursor: string;
      };
    };
  };
}

const GithubTemplateRepositoriesSchema = makeFieldSchemaFromZod(
  z.object({
    repositoryUrl: z.string(),
  }),
  z.object({}),
);

type GithubTemplateRepositoriesSchemaProps = typeof GithubTemplateRepositoriesSchema.type;
const GithubTemplateRepositoriesSchemaSchema = GithubTemplateRepositoriesSchema.schema;

const GithubTemplateRepositories = ({ onChange, required, schema, name, formData }: GithubTemplateRepositoriesSchemaProps) => {
  const title = schema.title || name || 'Github Template Repositories';
  const configApi = useApi(configApiRef);
  const identityApi = useApi(identityApiRef);
  const url = `${configApi.getString('backend.baseUrl')}/api/proxy/api-github/graphql`;
  const { value: repositories, loading, error } = useAsync(async () => {
    const { token } = await identityApi.getCredentials();
    if (!token) {
      throw new Error("Couldn't create a personal token.");
    }
    return await getTemplateRepositories(url, token);
  }, []);

  if (error) return <Typography color={'red'} variant='subtitle1'>error: {error.message}</Typography>;
  const currentValue = formData?.repositoryUrl ? repositories?.find((repo: Repository) => repo.url === formData.repositoryUrl) || null : null;

  return (
    <FormControl>
      <Autocomplete
        loading={loading}
        options={repositories || []}
        getOptionLabel={(repository: Repository) => repository.name}
        getOptionSelected={(option: Repository, value: Repository) => option.name === value.name}
        value={currentValue}
        renderInput={params => (
          <TextField
            {...params}
            label={title}
            variant="outlined"
            required={required}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <React.Fragment>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </React.Fragment>
              ),
            }}
          />
        )}
        onChange={(_, repository: Repository | null) => {
          return repository
            ? onChange({
              repositoryUrl: repository.url,
            })
            : onChange(undefined);
        }}
      />
    </FormControl>
  );
};

export const GithubTemplateRepositoriesExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'GithubTemplateRepositories',
    component: GithubTemplateRepositories,
    schema: GithubTemplateRepositoriesSchemaSchema,
  }),
);

async function getTemplateRepositories(url: string, ghToken: string): Promise<Repository[]> {
  const templateRepos: Repository[] = [];
  let hasNextPage = true;
  let cursor: string = "";
  while (hasNextPage) {
    console.log("Fetching repositories with cursor:", cursor);
    const query = `
      {
        search(
          type: REPOSITORY
          query: "org:TRANZACT archived:false template:true"
          first: 100
          after: "${cursor}"
          ) {
            repositoryCount
            pageInfo {
              hasNextPage
              endCursor
            }
            nodes {
              ... on Repository {
                name
                url
              }
            }
          }
      }
    `;
    const response = await axios.post<GraphQLResponse>(url, {
      query,
    }, {
      headers: {
        'Authorization': `Bearer ${ghToken}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      }
    });
    if (response.status !== HttpStatusCode.Ok) {
      throw new Error(`Failed to fetch repositories: ${response.statusText}`);
    }

    const repos = response.data.data.search.nodes;
    if (repos) {
      templateRepos.push(...repos);
      hasNextPage = response.data.data.search.pageInfo.hasNextPage;
      cursor = response.data.data.search.pageInfo.endCursor;
    } else {
      break;
    }
  }
  return templateRepos;
}
